// src/pages/admin/DataIntegrityPage.tsx
import { useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { DataRepairService, DataIntegrityReport, FinancialStructureIssue } from '../../utils/dataRepair';

export default function DataIntegrityPage() {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [issues, setIssues] = useState<FinancialStructureIssue[]>([]);
  const [report, setReport] = useState<DataIntegrityReport | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleScanPatients = async () => {
    setScanning(true);
    setIssues([]);
    setReport(null);

    try {
      const foundIssues = await DataRepairService.scanAllPatients();
      setIssues(foundIssues);

      if (foundIssues.length === 0) {
        alert('✅ Todos os pacientes estão com estruturas válidas!');
      } else {
        alert(`⚠️ Encontrados ${foundIssues.length} pacientes com problemas na estrutura de dados.`);
      }
    } catch (error) {
      console.error('Erro ao escanear pacientes:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`❌ Erro ao escanear pacientes: ${errorMessage}`);
    } finally {
      setScanning(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setReport(null);

    try {
      const integrityReport = await DataRepairService.generateIntegrityReport();
      setReport(integrityReport);
      setShowDetails(true);

      const foundIssues = integrityReport.details.map(detail => ({
        patientId: detail.patientId,
        patientName: detail.patientName,
        missingFinanceiro: detail.issues.includes('Objeto financeiro ausente'),
        missingFaturas: detail.issues.includes('Array de faturas ausente'),
        missingControleFaltas: detail.issues.includes('Controle de faltas ausente')
      }));
      setIssues(foundIssues);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`❌ Erro ao gerar relatório: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRepairAll = async () => {
    if (!confirm('⚠️ Deseja realmente reparar TODOS os pacientes com problemas? Esta ação modificará os dados no Firebase.')) {
      return;
    }

    setRepairing(true);
    setReport(null);

    try {
      const repairReport = await DataRepairService.executeFullRepair();
      setReport(repairReport);
      setShowDetails(true);
      alert(`✅ Reparo concluído!\n\n${repairReport.patientsRepaired} pacientes foram reparados com sucesso.`);
    } catch (error) {
      console.error('Erro ao reparar pacientes:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`❌ Erro ao reparar pacientes: ${errorMessage}`);
    } finally {
      setRepairing(false);
    }
  };

  const handleRepairSingle = async (patientId: string) => {
    if (!confirm(`Deseja reparar a estrutura do paciente ${patientId}?`)) {
      return;
    }

    try {
      await DataRepairService.repairPatientStructure(patientId);
      alert('✅ Paciente reparado com sucesso!');

      // Atualizar lista
      await handleScanPatients();
    } catch (error) {
      console.error('Erro ao reparar paciente:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`❌ Erro: ${errorMessage}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Integridade de Dados
              </h1>
              <p className="text-gray-600">
                Validação e reparo de estruturas financeiras dos pacientes
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">O que este utilitário faz?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Escaneia todos os pacientes em busca de estruturas financeiras incompletas</li>
                  <li>Identifica campos ausentes (financeiro, faturas, controleFaltas)</li>
                  <li>Permite reparar automaticamente as estruturas com valores padrão seguros</li>
                  <li>Previne erros "undefined field value" no Firebase</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Ações</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleScanPatients}
              disabled={scanning || loading || repairing}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Escaneando...</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5" />
                  <span>Escanear Pacientes</span>
                </>
              )}
            </button>

            <button
              onClick={handleGenerateReport}
              disabled={scanning || loading || repairing}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Gerando...</span>
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  <span>Gerar Relatório</span>
                </>
              )}
            </button>

            <button
              onClick={handleRepairAll}
              disabled={scanning || loading || repairing || (!report || report.patientsNeedingRepair === 0)}

              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {repairing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Reparando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Reparar Todos</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Issues List */}
        {issues.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Pacientes com Problemas ({issues.length})
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nome
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Problemas
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {issues.map((issue) => (
                    <tr key={issue.patientId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        {issue.patientId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {issue.patientName}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="space-y-1">
                          {issue.missingFinanceiro && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Objeto financeiro ausente
                            </span>
                          )}
                          {issue.missingFaturas && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ml-2">
                              Array faturas ausente
                            </span>
                          )}
                          {issue.missingControleFaltas && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 ml-2">
                              Controle faltas ausente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleRepairSingle(issue.patientId)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Reparar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Report */}
        {report && showDetails && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Relatório de Integridade
            </h2>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 mb-1">Total de Pacientes</p>
                <p className="text-2xl font-bold text-blue-900">{report.totalPatients}</p>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-600 mb-1">Com Problemas</p>
                <p className="text-2xl font-bold text-orange-900">{report.patientsNeedingRepair}</p>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 mb-1">Reparados</p>
                <p className="text-2xl font-bold text-green-900">{report.patientsRepaired}</p>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-600 mb-1">Erros</p>
                <p className="text-2xl font-bold text-red-900">{report.errors.length}</p>
              </div>
            </div>

            {/* Details */}
            {report.details.length > 0 && (
              <div className="mt-6">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Detalhes</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Paciente
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Problemas Encontrados
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {report.details.map((detail, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {detail.patientName}
                            <div className="text-xs text-gray-500 font-mono">{detail.patientId}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <ul className="list-disc list-inside">
                              {detail.issues.map((issue, i) => (
                                <li key={i}>{issue}</li>
                              ))}
                            </ul>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {detail.repaired ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✅ Reparado
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                ⚠️ Pendente
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Errors */}
            {report.errors.length > 0 && (
              <div className="mt-6">
                <h3 className="text-md font-semibold text-red-900 mb-3">Erros</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <ul className="space-y-2">
                    {report.errors.map((error, index) => (
                      <li key={index} className="text-sm text-red-800">
                        <span className="font-mono text-xs text-red-600">{error.patientId}</span>: {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Issues Message */}
        {issues.length === 0 && !report && !scanning && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum Escaneamento Realizado
            </h3>
            <p className="text-gray-600">
              Clique em "Escanear Pacientes" para verificar a integridade dos dados.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
